import React, { useState, useEffect, useCallback, useRef } from 'react';
import { attachHereMap, detachHereMap } from '../lib/hereMap';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { TakeittoLogo } from '../components/TakeittoLogo';
import { 
  Package, Truck, MapPin, Clock, DollarSign, Weight, 
  RefreshCw, LogOut, CheckCircle, Navigation, User, 
  Filter, Key, Copy, Settings, Phone, ExternalLink, Camera
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERE_API_KEY = process.env.REACT_APP_HERE_API_KEY;

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    assigned: { label: 'Assigned', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    in_transit: { label: 'In Transit', className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    delivered: { label: 'Delivered', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    delayed: { label: 'Delayed', className: 'bg-red-100 text-red-700 border-red-200' }
  };
  
  const { label, className } = config[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${className}`}>
      {label}
    </span>
  );
};

// Order card component
const OrderCard = ({ order, onSelect, isSelected, drivers, onStatusChange, onCopyTrackingLink }) => {
  const assignedDriver = drivers.find(d => d.id === order.assigned_driver_id);
  
  return (
    <div 
      className={`order-card p-3 bg-white rounded-lg cursor-pointer border-l-4 ${
        isSelected ? 'ring-2 ring-indigo-500' : ''
      }`}
      style={{
        borderLeftColor: {
          pending: '#F59E0B',
          assigned: '#3B82F6',
          in_transit: '#6366F1',
          delivered: '#10B981',
          delayed: '#EF4444'
        }[order.status] || '#6B7280',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)'
      }}
      onClick={() => onSelect(order)}
      data-testid={`order-card-${order.id}`}
    >
      <div className="flex justify-between items-start gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs text-gray-500">{order.id.slice(0, 8)}</p>
          <h4 className="font-semibold text-gray-900 text-sm truncate">{order.customer_name}</h4>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={(e) => { e.stopPropagation(); onCopyTrackingLink(order.id); }}
            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-indigo-600"
            title="Copy tracking link"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
          <StatusBadge status={order.status} />
        </div>
      </div>
      
      <p className="text-xs text-gray-600 truncate mb-2">{order.delivery_address}</p>
      
      <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-2">
        <span className="flex items-center gap-1">
          <DollarSign className="w-3 h-3" />
          ${order.order_value?.toFixed(0) || 0}
        </span>
        <span className="flex items-center gap-1">
          <Weight className="w-3 h-3" />
          {order.total_weight}kg
        </span>
        <span className="flex items-center gap-1">
          <Navigation className="w-3 h-3" />
          {order.distance_from_dispatch}km
        </span>
      </div>
      
      {/* ETA Display for assigned/in_transit orders */}
      {order.eta_minutes && ['assigned', 'in_transit'].includes(order.status) && (
        <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded text-xs mb-2">
          <Clock className="w-3 h-3 text-indigo-600" />
          <span className="text-indigo-700 font-medium">
            ETA: {order.eta_minutes} min ({order.distance_km} km)
          </span>
        </div>
      )}
      
      {order.delivery_time_window && (
        <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
          <Clock className="w-3 h-3" />
          {order.delivery_time_window}
        </div>
      )}
      
      {assignedDriver && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded text-xs mb-2">
          <Truck className="w-3 h-3 text-blue-600" />
          <span className="text-blue-800 truncate">{assignedDriver.name}</span>
        </div>
      )}
      
      {order.status === 'in_transit' && (
        <Button 
          size="sm" 
          className="mt-1 w-full h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
          onClick={(e) => { e.stopPropagation(); onStatusChange(order.id, 'delivered'); }}
          data-testid={`mark-delivered-${order.id}`}
        >
          Mark Delivered
        </Button>
      )}
      
      {/* Delivery Photo Proof for delivered orders */}
      {order.status === 'delivered' && order.delivery_photo_path && (
        <div className="mt-2 p-2 bg-emerald-50 rounded border border-emerald-100">
          <div className="flex items-center gap-2 mb-2">
            <Camera className="w-3 h-3 text-emerald-600" />
            <span className="text-xs text-emerald-700 font-medium">Delivery Proof</span>
          </div>
          <img 
            src={`${API}/orders/${order.id}/delivery-photo`}
            alt="Delivery proof"
            className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`${API}/orders/${order.id}/delivery-photo`, '_blank');
            }}
            data-testid={`delivery-photo-${order.id}`}
          />
        </div>
      )}
    </div>
  );
};

// Driver card component
const DriverCard = ({ driver, onSelect, isSelected }) => {
  const capacityUsed = (driver.current_load / driver.max_capacity) * 100;
  
  return (
    <div 
      className={`p-3 bg-white rounded-lg border cursor-pointer ${
        isSelected ? 'ring-2 ring-indigo-500' : ''
      } ${driver.available ? 'border-emerald-200' : 'border-gray-200 opacity-60'}`}
      onClick={() => onSelect(driver)}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          driver.available ? 'bg-emerald-100' : 'bg-gray-100'
        }`}>
          <Truck className={`w-5 h-5 ${driver.available ? 'text-emerald-600' : 'text-gray-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 text-sm truncate">{driver.name}</h4>
          <p className="text-xs text-gray-500">{driver.phone}</p>
        </div>
        <div className={`w-2 h-2 rounded-full ${driver.available ? 'bg-emerald-500' : 'bg-gray-300'}`} />
      </div>
      
      <div className="mt-3">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-500">Capacity</span>
          <span className="font-medium">{driver.current_load}/{driver.max_capacity} kg</span>
        </div>
        <Progress value={capacityUsed} className="h-1.5" />
      </div>
      
      <div className="mt-2 text-xs text-gray-500">
        {driver.city}
      </div>
    </div>
  );
};

// HERE Map Component
// Map viewport helpers: keep the map focused on Australia so a single
// mis-geocoded point can't zoom the view out to the whole world
const AUSTRALIA_BOUNDS = { north: -10.0, south: -44.0, west: 112.5, east: 154.0 };
const CITY_FIT_RADIUS_KM = 150;
const MAP_PADDING = 60;

const isInAustralia = (lat, lng) =>
  lat <= AUSTRALIA_BOUNDS.north && lat >= AUSTRALIA_BOUNDS.south &&
  lng >= AUSTRALIA_BOUNDS.west && lng <= AUSTRALIA_BOUNDS.east;

const distanceKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Fit the map to the given points; use the fallback view when there are no points
const fitMapToPoints = (map, points, fallback) => {
  if (points.length === 0) {
    if (fallback?.bounds) {
      const b = fallback.bounds;
      map.getViewModel().setLookAtData({ bounds: new window.H.geo.Rect(b.north, b.west, b.south, b.east) }, true);
    } else if (fallback?.center) {
      map.setCenter(fallback.center, true);
      map.setZoom(fallback.zoom || 11, true);
    }
    return;
  }
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  points.forEach(p => {
    minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng); maxLng = Math.max(maxLng, p.lng);
  });
  // A single point (or points on top of each other) has no area to fit, so centre on it instead
  if (maxLat - minLat < 0.005 && maxLng - minLng < 0.005) {
    map.setCenter({ lat: points[0].lat, lng: points[0].lng }, true);
    map.setZoom(13, true);
    return;
  }
  map.getViewModel().setLookAtData({ bounds: new window.H.geo.Rect(maxLat, minLng, minLat, maxLng) }, true);
};

const MerchantMap = ({ orders, drivers, selectedOrder, selectedDriver }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    
    const initMap = () => {
      if (typeof window.H === 'undefined') return;
      
      try {
        const map = attachHereMap('merchant', mapRef.current, (element) => {
          const platform = new window.H.service.Platform({ apikey: HERE_API_KEY });
          const defaultLayers = platform.createDefaultLayers();
          
          const newMap = new window.H.Map(
            element,
            defaultLayers.vector.normal.map,
            { center: { lat: -33.8688, lng: 151.2093 }, zoom: 5, pixelRatio: window.devicePixelRatio || 1 }
          );
          
          new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(newMap));
          window.H.ui.UI.createDefault(newMap, defaultLayers);
          
          // Keep markers clear of the map edges and overlays when fitting the view
          newMap.getViewPort().setPadding(MAP_PADDING, MAP_PADDING, MAP_PADDING, MAP_PADDING);
          return newMap;
        });
        mapInstanceRef.current = map;
        setMapReady(true);
      } catch (err) {
        console.error('Map init error:', err);
      }
    };
    
    const timeoutId = setTimeout(initMap, 500);
    
    return () => {
      clearTimeout(timeoutId);
      if (mapInstanceRef.current) detachHereMap('merchant');
      mapInstanceRef.current = null;
    };
  }, []);

  // Update markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    
    markersRef.current.forEach(marker => {
      try { map.removeObject(marker); } catch (e) {}
    });
    markersRef.current = [];
    
    // Add order markers
    orders.forEach(order => {
      if (!order.latitude || !order.longitude) return;
      
      const isSelected = selectedOrder?.id === order.id;
      const color = { pending: '#F59E0B', assigned: '#3B82F6', in_transit: '#6366F1', delivered: '#10B981' }[order.status] || '#6B7280';
      const size = isSelected ? 32 : 24;
      
      const icon = new window.H.map.Icon(
        `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
          <circle cx="${size/2}" cy="${size/2}" r="${size/2-2}" fill="white" stroke="${color}" stroke-width="3"/>
          <circle cx="${size/2}" cy="${size/2}" r="${size/4}" fill="${color}"/>
        </svg>`,
        { size: { w: size, h: size }, anchor: { x: size/2, y: size/2 } }
      );
      
      const marker = new window.H.map.Marker({ lat: order.latitude, lng: order.longitude }, { icon });
      map.addObject(marker);
      markersRef.current.push(marker);
    });
    
    // Add driver markers
    drivers.forEach(driver => {
      if (!driver.latitude || !driver.longitude) return;
      
      const isSelected = selectedDriver?.id === driver.id;
      const size = isSelected ? 36 : 28;
      
      const icon = new window.H.map.Icon(
        `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
          <circle cx="${size/2}" cy="${size/2}" r="${size/2-2}" fill="#6366F1" stroke="white" stroke-width="2"/>
          <rect x="${size/2-6}" y="${size/2-3}" width="12" height="6" rx="1" fill="white"/>
          <circle cx="${size/2-3}" cy="${size/2+3}" r="2" fill="white"/>
          <circle cx="${size/2+3}" cy="${size/2+3}" r="2" fill="white"/>
        </svg>`,
        { size: { w: size, h: size }, anchor: { x: size/2, y: size/2 } }
      );
      
      const marker = new window.H.map.Marker({ lat: driver.latitude, lng: driver.longitude }, { icon });
      map.addObject(marker);
      markersRef.current.push(marker);
    });
    
    // Fit bounds - ignore points outside Australia so one bad address can't zoom out to the world
    const points = markersRef.current
      .map(marker => marker.getGeometry())
      .filter(pos => pos && isInAustralia(pos.lat, pos.lng));
    fitMapToPoints(map, points, { bounds: AUSTRALIA_BOUNDS });
  }, [orders, drivers, selectedOrder, selectedDriver, mapReady]);

  return (
    <div ref={mapRef} className="w-full h-full" style={{ background: '#e5e5e5' }} />
  );
};

export default function MerchantDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [merchant, setMerchant] = useState(null);
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [apiKeys, setApiKeys] = useState(null);
  const [stats, setStats] = useState(null);
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('orders');
  
  const [isLoading, setIsLoading] = useState(true);
  
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [merchantRes, ordersRes, driversRes, statsRes] = await Promise.all([
        axios.get(`${API}/merchants/me`, { headers }),
        axios.get(`${API}/merchants/me/orders`, { headers }),
        axios.get(`${API}/personnel`, { headers }),
        axios.get(`${API}/merchants/me/stats`, { headers })
      ]);
      
      setMerchant(merchantRes.data);
      setOrders(ordersRes.data);
      setDrivers(driversRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, navigate]);

  const fetchApiKeys = async () => {
    try {
      const res = await axios.get(`${API}/merchants/me/api-keys`, { headers });
      setApiKeys(res.data);
    } catch (error) {
      console.error('Failed to fetch API keys');
    }
  };

  const regenerateApiKeys = async () => {
    if (!window.confirm('Are you sure? This will invalidate your current API key.')) return;
    
    try {
      const res = await axios.post(`${API}/merchants/me/api-keys/regenerate`, {}, { headers });
      setApiKeys(res.data);
      toast.success('API keys regenerated');
    } catch (error) {
      toast.error('Failed to regenerate keys');
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'api') {
      fetchApiKeys();
    }
  }, [activeTab]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.post(`${API}/orders/${orderId}/status/${newStatus}`, {}, { headers });
      toast.success('Status updated');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const copyTrackingLink = (orderId) => {
    const trackingCode = orderId.slice(0, 8);
    const url = `${window.location.origin}/track/${trackingCode}`;
    navigator.clipboard.writeText(url);
    toast.success('Tracking link copied!');
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const filteredOrders = orders.filter(order => {
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    return true;
  });

  const availableDrivers = drivers.filter(d => d.available).length;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden bg-gray-100" data-testid="merchant-dashboard">
      {/* Header */}
      <header className="bg-white border-b px-3 sm:px-4 py-2 flex items-center justify-between gap-2 shrink-0 z-50">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <TakeittoLogo size="sm" />
          <div className="h-6 w-px bg-gray-200 hidden sm:block" />
          <span className="font-semibold text-gray-900 truncate">{merchant?.business_name}</span>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Button variant="outline" size="sm" onClick={fetchData} title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">
                {user?.name?.charAt(0) || 'M'}
              </span>
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">Merchant</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>
      
      {/* Stats Bar */}
      <div className="bg-white border-b px-3 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 shrink-0">
        <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-1 text-sm">
          <span className="flex items-center gap-1">
            <Package className="w-4 h-4 text-gray-500" />
            Orders: <strong>{stats?.total_orders || 0}</strong>
          </span>
          <span className="flex items-center gap-1 text-amber-600">
            <Clock className="w-4 h-4" />
            Pending: <strong>{stats?.pending_orders || 0}</strong>
          </span>
          <span className="flex items-center gap-1 text-indigo-600">
            <Navigation className="w-4 h-4" />
            Transit: <strong>{stats?.in_transit || 0}</strong>
          </span>
          <span className="flex items-center gap-1 text-emerald-600">
            <CheckCircle className="w-4 h-4" />
            Done: <strong>{stats?.delivered || 0}</strong>
          </span>
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Truck className="w-4 h-4 text-indigo-600" />
          Drivers: <strong className="text-indigo-600">{availableDrivers}/{drivers.length}</strong>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row">
        {/* Sidebar - below the map on phones, beside it from tablet width */}
        <div className="order-2 md:order-1 flex-1 min-h-0 md:flex-none md:w-[340px] lg:w-[380px] bg-white border-t md:border-t-0 md:border-r flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
            <TabsList className="w-full rounded-none border-b h-10 bg-gray-50">
              <TabsTrigger value="orders" className="flex-1 text-xs"><Package className="w-3 h-3 mr-1" />Orders</TabsTrigger>
              <TabsTrigger value="drivers" className="flex-1 text-xs"><Truck className="w-3 h-3 mr-1" />Drivers</TabsTrigger>
              <TabsTrigger value="api" className="flex-1 text-xs"><Key className="w-3 h-3 mr-1" />API</TabsTrigger>
            </TabsList>
            
            {/* Orders Tab */}
            <TabsContent value="orders" className="flex-1 m-0 flex flex-col overflow-hidden">
              <div className="p-2 border-b flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="in_transit">In Transit</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="px-2 py-1 text-xs text-gray-500 border-b">{filteredOrders.length} orders</div>
              
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {filteredOrders.map(order => (
                  <OrderCard 
                    key={order.id} 
                    order={order} 
                    drivers={drivers} 
                    isSelected={selectedOrder?.id === order.id} 
                    onSelect={setSelectedOrder} 
                    onStatusChange={handleStatusChange} 
                    onCopyTrackingLink={copyTrackingLink} 
                  />
                ))}
                {filteredOrders.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No orders found</p>
                    <p className="text-xs mt-1">Orders from your API will appear here</p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Drivers Tab */}
            <TabsContent value="drivers" className="flex-1 m-0 flex flex-col overflow-hidden">
              <div className="px-3 py-2 border-b">
                <p className="text-xs text-gray-500">
                  {availableDrivers} available, {drivers.length - availableDrivers} busy
                </p>
              </div>
              
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {drivers.map(driver => (
                  <DriverCard 
                    key={driver.id} 
                    driver={driver} 
                    isSelected={selectedDriver?.id === driver.id}
                    onSelect={setSelectedDriver}
                  />
                ))}
                {drivers.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No drivers available</p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* API Tab */}
            <TabsContent value="api" className="flex-1 m-0 overflow-y-auto p-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4 text-indigo-600" />
                  API Credentials
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500">API Key</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={apiKeys?.api_key || '••••••••••••••••'}
                        readOnly
                        className="font-mono text-xs"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(apiKeys?.api_key, 'API Key')}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500">Webhook Secret</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={apiKeys?.webhook_secret || '••••••••••••••••'}
                        readOnly
                        className="font-mono text-xs"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(apiKeys?.webhook_secret, 'Secret')}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-red-600 border-red-200 hover:bg-red-50"
                    onClick={regenerateApiKeys}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate Keys
                  </Button>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">API Endpoint</h3>
                <code className="block bg-gray-900 text-emerald-400 p-3 rounded text-xs">
                  POST /api/webhook/orders
                </code>
                
                <h4 className="font-medium text-gray-900 mt-4 mb-2">Example Request</h4>
                <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto">
{`curl -X POST "${process.env.REACT_APP_BACKEND_URL}/api/webhook/orders" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "John Doe",
    "delivery_address": "123 George St, Sydney",
    "order_value": 85.50,
    "total_weight": 2.5
  }'`}
                </pre>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-indigo-600" />
                  Business Info
                </h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Business</span>
                    <span className="font-medium">{merchant?.business_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Plan</span>
                    <span className="font-medium capitalize">{merchant?.plan}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">City</span>
                    <span className="font-medium">{merchant?.city}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">API Calls</span>
                    <span className="font-medium">{stats?.api_calls_this_month || 0} this month</span>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Map */}
        <div className="order-1 md:order-2 relative h-[40dvh] shrink-0 md:h-auto md:flex-1 md:shrink min-w-0">
          <MerchantMap 
            orders={filteredOrders}
            drivers={drivers}
            selectedOrder={selectedOrder}
            selectedDriver={selectedDriver}
          />
          
          {/* Legend */}
          <div className="hidden sm:block absolute bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
            <h4 className="font-semibold text-xs mb-2">Legend</h4>
            <div className="space-y-1 text-[10px]">
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-indigo-600" /><span>Driver</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-amber-500 bg-white" /><span>Pending</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-blue-500 bg-white" /><span>Assigned</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-indigo-500 bg-white" /><span>In Transit</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-emerald-500 bg-white" /><span>Delivered</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
